import { getMessageWithPlaceholders } from 'shared-locales';
import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getBlockNameByType } from 'lib/blocks';
import { getFormattedDate } from 'lib/common';
import {
  FillInSummary,
  LikertSummary,
  MultipleChoiceSummary,
  ScormSummary,
  SequencingSummary,
  TaskSummaryItem,
  TaskQuestion
} from '.';

interface Props {
  courseSlug?: string;
  task: any;
}

const TaskSummary = ({ courseSlug, task }: Props) => {
  const { dateNow } = useDateNow();

  const { messages, theme } = useReduxState();

  let { isSingleChoice, pointsWeight } = task.data;
  if (!task.data.hasOwnProperty('pointsWeight')) {
    if (task.type === 'openquestion') pointsWeight = 5;
    else if (task.type === 'scorm') pointsWeight = 10;
    else pointsWeight = 2;
  }

  const headerTaskText =
    pointsWeight === 0
      ? messages.commonNoPoints
      : getMessageWithPlaceholders({
          message: messages.commonNumberOfPoints,
          placeholders: {
            numberOfPoints: pointsWeight
          }
        });

  const h5pVersion2 = task.type === 'h5p' && task.data?.version > 1;

  return (
    <>
      {task.data.isTask || !task.data.hasOwnProperty('isTask') ? (
        <div className="task">
          {task.data.visibility === 'hidden' ? (
            <div className="hidden">{messages.tasksQuestionHidden}</div>
          ) : !task.chapter.publishDate ? (
            <div className="hidden">{messages.tasksQuestionUnpublished}</div>
          ) : Date.parse(task.chapter.publishDate) > dateNow ? (
            <div className="hidden">
              {`${messages.commonWillPublish} ${getFormattedDate({
                date: task.chapter.publishDate
              })}`}
            </div>
          ) : null}

          <div className="header">
            <div className="title">
              <span>
                {getBlockNameByType({
                  messages,
                  type: isSingleChoice ? 'singlechoice' : task.type
                })}
              </span>

              {h5pVersion2 ? <span>{' (v2)'}</span> : null}

              {task.data?.isMandatory ? (
                <span className="title-mandatory">
                  <span className="title-divider">{'•'}</span>
                  <span>{messages.commonMandatory}</span>
                </span>
              ) : null}

              <span className="title-points">{`(${headerTaskText})`}</span>
            </div>

            <div className="info">
              {task.type === 'openquestion' && task.averageScore ? (
                <TaskSummaryItem
                  label={messages.tasksAverageScore}
                  value={task.averageScore || '–'}
                />
              ) : null}

              <TaskSummaryItem
                label={messages.tasksNumberOfAnswers}
                value={task.answers ? task.answers.length : 0}
                tooltip={
                  task.unreviewedAnswers
                    ? `${task.unreviewedAnswers} ${messages.coursesUnreviewedAnswers}`
                    : null
                }
                unreadItems={task.unreviewedAnswers}
              />
            </div>
          </div>

          <TaskQuestion
            question={
              task.type === 'likert'
                ? task.data.description
                : task.data.question
            }
          />

          {task.type === 'fillin' ? (
            <FillInSummary inAnswerView={!courseSlug} task={task} />
          ) : task.type === 'likert' ? (
            <LikertSummary task={task} />
          ) : task.type === 'multiplechoice' ? (
            <MultipleChoiceSummary task={task} />
          ) : task.type === 'scorm' || task.type === 'h5p' ? (
            <ScormSummary task={task} courseSlug={courseSlug} />
          ) : task.type === 'sequencing' ? (
            <SequencingSummary task={task} />
          ) : null}

          <style jsx>{`
            .task {
              background-color: ${theme.lightGrey};
              border-radius: ${theme.cardBorderRadius};
              padding: 15px;
            }
            .header {
              margin-bottom: 15px;
            }
            .title {
              color: ${theme.textColorLight};
              flex-grow: 1;
              font-size: 1.5rem;
              font-weight: 600;
              min-width: 0;
            }
            .title-mandatory {
              color: ${theme.textColorLighter};
              font-weight: 400;
            }
            .title-divider {
              margin: 0 5px;
            }
            .title-points {
              margin-left: 10px;
            }
            .hidden {
              color: ${theme.textColorLighter};
              display: inline-block;
              font-size: 1.3rem;
              font-weight: 400;
              margin-bottom: 15px;
            }
            @media (max-width: ${breakpoints.small - 1}px) {
              .info {
                margin-top: 15px;
              }
            }
            @media (min-width: ${breakpoints.small}px) {
              .header {
                align-items: center;
                display: flex;
              }
            }
          `}</style>
        </div>
      ) : null}
    </>
  );
};

export default TaskSummary;
